import React, {useState} from "react"
import {Alert, CircularProgress, Divider, IconButton, Stack, Typography, useTheme} from "@mui/joy"
import {Eye, EyeOff, Gift} from "lucide-react"
import Button from "./Button"
import GoogleIcon from "./GoogleIcon"
import Input from "./Input"
import {useAuth} from "../hooks/useAuth"
import {authErrorMessage} from "../context/AuthContext"
import {useAsync} from "../hooks/useAsyncResult"
import {validateReferralCode} from "../services/api/referrals"
import {storedReferralCode} from "../services/referral"
import {activePalette, applyTransparency} from "../theme"
import {useTranslation} from "react-i18next"

export type AuthMode = "signin" | "signup"

export type AuthFormProps = {
    /** Mode the form opens on */
    initialMode?: AuthMode
    /** Called right after the account is signed in, before the context state settles */
    onAuthenticated?: () => void
}

const MIN_PASSWORD_LENGTH = 8

/**
 * Sign in and signup, without the surface around them.
 *
 * Kept surface free on purpose: the same form is the whole auth page and the body of the auth
 * dialog, and each of those brings its own container.
 */
const AuthForm = ({initialMode = "signin", onAuthenticated}: AuthFormProps) => {
    const theme = useTheme()
    const palette = activePalette(theme)
    const auth = useAuth()
    const {t} = useTranslation()
    const referralCheck = useAsync(validateReferralCode, false, false, false)

    const [mode, setMode] = useState<AuthMode>(initialMode)
    const [name, setName] = useState("")
    const [email, setEmail] = useState("")
    const [password, setPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")
    const [referralCode, setReferralCode] = useState(storedReferralCode() || "")
    const [showPassword, setShowPassword] = useState(false)
    const [error, setError] = useState("")
    const [notice, setNotice] = useState("")

    const isSignUp = mode === "signup"
    const referral = referralCheck.data?.response?.[0]

    const switchMode = (next: AuthMode) => {
        // A code carried by a share link is confirmed as soon as the form that uses it opens
        if (next === "signup" && referralCode.trim())
            referralCheck.execute(referralCode.trim())

        setMode(next)
        setError("")
        setNotice("")
        setPassword("")
        setConfirmPassword("")
    }

    /** Everything the form can catch before a request goes out */
    const validate = (): string => {
        if (!email.trim()) return t("enterEmail")
        if (!password) return t("enterPassword")

        if (!isSignUp) return ""

        if (!name.trim()) return t("enterName")
        if (password.length < MIN_PASSWORD_LENGTH) return `Your password needs at least ${MIN_PASSWORD_LENGTH} characters`
        if (password !== confirmPassword) return t("passwordsMustMatch")

        return ""
    }

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault()
        setNotice("")

        const validationError = validate()
        if (validationError) {
            setError(validationError)
            return
        }

        setError("")

        try {
            if (isSignUp)
                await auth.register?.({
                    name: name.trim(),
                    email: email.trim(),
                    password,
                    referralCode: referralCode.trim() || undefined
                } as any)
            else
                await auth.signIn?.(email.trim(), password)

            onAuthenticated?.()
        } catch (e) {
            setError(authErrorMessage(e))
        }
    }

    const handleGoogle = async () => {
        setError("")
        setNotice("")

        try {
            await auth.signInWithGoogle?.()
            onAuthenticated?.()
        } catch (e) {
            setError(authErrorMessage(e))
        }
    }

    const handlePasswordReset = async () => {
        if (!email.trim()) {
            setError(t("resetEmailFirst"))
            return
        }

        setError("")

        try {
            await auth.resetPassword?.(email.trim())
            setNotice(t("resetLinkSent"))
        } catch (e) {
            setError(authErrorMessage(e))
        }
    }

    return <Stack gap={2}>
        {/* Mode switch */}
        <Stack direction={"row"} padding={0.5} gap={0.5}
               sx={{
                   borderRadius: "13px",
                   backgroundColor: "rgba(255,255,255,0.05)",
                   border: "1px solid rgba(255,255,255,0.08)"
               }}>
            {([["signin", t("signInTab")], ["signup", t("signUpTab")]] as [AuthMode, string][]).map(([value, label]) =>
                <Stack key={value} flex={1} alignItems={"center"} paddingY={1}
                       onClick={() => switchMode(value)}
                       sx={{
                           cursor: "pointer",
                           borderRadius: "10px",
                           backgroundColor: mode === value ? applyTransparency(palette.primary["500"], 0.35) : "transparent",
                           "&:hover": {backgroundColor: mode === value ? undefined : "rgba(255,255,255,0.06)"}
                       }}>
                    <Typography level={"title-sm"}
                                textColor={mode === value ? "white" : "text.tertiary"}>
                        {label}
                    </Typography>
                </Stack>)}
        </Stack>

        {error && <Alert color={"danger"} variant={"soft"}>{error}</Alert>}
        {notice && <Alert color={"success"} variant={"soft"}>{notice}</Alert>}

        <Stack component={"form"} onSubmit={handleSubmit} gap={2}>
            {isSignUp && <Input placeholder={t("fullNamePlaceholder")} autoComplete={"name"} value={name}
                                onChange={e => setName(e.target.value)}/>}

            <Input placeholder={t("emailPlaceholder")} type={"email"} autoComplete={"email"} value={email}
                   onChange={e => setEmail(e.target.value)}/>

            <Input placeholder={t("passwordPlaceholder")} type={showPassword ? "text" : "password"}
                   autoComplete={isSignUp ? "new-password" : "current-password"} value={password}
                   onChange={e => setPassword(e.target.value)}
                   endDecorator={<IconButton variant={"plain"} size={"sm"} type={"button"}
                                             onClick={() => setShowPassword(!showPassword)}>
                       {showPassword ? <EyeOff/> : <Eye/>}
                   </IconButton>}/>

            {isSignUp && <Input placeholder={t("confirmPasswordPlaceholder")} type={showPassword ? "text" : "password"}
                                autoComplete={"new-password"} value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}/>}

            {isSignUp && <Stack gap={1}>
                <Input placeholder={t("referralCodePlaceholder")} value={referralCode}
                       onChange={e => setReferralCode(e.target.value.toUpperCase())}
                       onBlur={() => referralCode.trim() && referralCheck.execute(referralCode.trim())}
                       endDecorator={referralCheck.loading && <CircularProgress size={"sm"}/>}/>

                {referral && referralCode.trim() && (referral.valid
                    ? <Typography level={"body-xs"} startDecorator={<Gift size={14}/>} textColor={"success.400"}>
                        {referral.referrerName
                            ? t("invitedYou", {name: referral.referrerName})
                            : t("validCode")} ·
                        {" "}{t("discountOffFirstPlan", {percent: referral.discountPercent})}
                    </Typography>
                    : <Typography level={"body-xs"} textColor={"danger.400"}>
                        {t("invalidReferralCode")}
                    </Typography>)}
            </Stack>}

            <Button type={"submit"} loading={auth.submitting} sx={{paddingY: 1.5}}>
                {isSignUp ? t("createMyAccount") : t("signIn")}
            </Button>

            {!isSignUp && <Typography level={"body-xs"} textAlign={"center"} onClick={handlePasswordReset}
                                      sx={{cursor: "pointer", "&:hover": {color: "white"}}}>
                {t("forgotPassword")}
            </Typography>}
        </Stack>

        <Divider>
            <Typography level={"body-xs"} textColor={"text.tertiary"}>{t("orSeparator")}</Typography>
        </Divider>

        {/* Google keeps its own surface: recolouring it would break the brand it is recognised by */}
        <Button onClick={handleGoogle} disabled={auth.submitting} startDecorator={<GoogleIcon/>}
                sx={{
                    background: "white",
                    color: "#1f1f1f",
                    paddingY: 1.5,
                    "&:hover": {background: "#f1f1f1"}
                }}>
            {t("continueWithGoogle")}
        </Button>

        {isSignUp && <Typography level={"body-xs"} textAlign={"center"} textColor={"text.tertiary"}>
            {t("termsNotice")}
        </Typography>}
    </Stack>
}

export default AuthForm
